import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getMyApplications } from '../../services/applicationService';
import { getAllJobs } from '../../services/jobService';
import StatsCard from './StatsCard';
import ApplicationCard from './ApplicationCard';
import JobCard from '../jobs/JobCard';
import { Briefcase, Clock, CheckCircle2, XCircle, Search, X, Zap, Globe, Building2, Terminal, Code, Palette, BarChart, Layers } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { motion, AnimatePresence } from 'framer-motion';
const JobSeekerDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [jobsPage, setJobsPage] = useState({ content: [], number: 0, totalPages: 1 });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('jobs');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);
  const [selectedRemote, setSelectedRemote] = useState('all'); // 'all', 'remote', 'onsite'
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('cat') || 'All');

  const navigate = useNavigate();

  const jobTypes = ['Full-time', 'Part-time', 'Freelance', 'Internship', 'Contract'];
  const categories = [
    { name: 'All', icon: <Layers size={16} /> },
    { name: 'Engineering', icon: <Code size={16} /> },
    { name: 'Design', icon: <Palette size={16} /> },
    { name: 'Data Science', icon: <BarChart size={16} /> },
    { name: 'DevOps', icon: <Terminal size={16} /> },
    { name: 'Management', icon: <Zap size={16} /> }
  ];

  const loadApps = async () => {
    try {
      const appsRes = await getMyApplications();
      const apps = appsRes?.data || (Array.isArray(appsRes) ? appsRes : []);
      setApplications(Array.isArray(apps) ? apps : []);
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: 9,
      };
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory !== 'All') params.category = selectedCategory;
      if (selectedJobTypes.length > 0) params.jobType = selectedJobTypes.join(',');
      if (selectedRemote !== 'all') params.remote = selectedRemote;

      const jobsData = await getAllJobs(params);
      
      const content = jobsData?.content || [];
      const totalPages = jobsData?.totalPages || 1;
      const number = jobsData?.number || 0;

      setJobsPage({ content, totalPages, number });
    } catch (err) {
      console.error('Error loading jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApps();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [currentPage, searchTerm, selectedCategory, selectedJobTypes, selectedRemote]);

  // Update URL when search or category changes
  useEffect(() => {
    const params = {};
    if (searchTerm) params.q = searchTerm;
    if (selectedCategory !== 'All') params.cat = selectedCategory;
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedCategory, setSearchParams]);

  const handleApply = async (jobId) => {
    loadApps();
    fetchJobs();
  };

  const toggleJobType = (type) => {
    setCurrentPage(0);
    setSelectedJobTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const clearAllFilters = () => {
    setCurrentPage(0);
    setSearchTerm('');
    setSelectedJobTypes([]);
    setSelectedRemote('all');
    setSelectedCategory('All');
  };

  // Memoize jobs with status for performance
  const jobsWithStatus = useMemo(() => {
    return (jobsPage.content || []).map(job => {
      const application = applications.find(app => app.jobId === job.jobPostId);
      return {
        ...job,
        applicationStatus: application ? application.status : null
      };
    });
  }, [jobsPage.content, applications]);

  const filteredJobs = jobsWithStatus;

  const stats = [
    { title: 'Applied', value: applications.length, icon: Briefcase, color: 'primary' },
    { title: 'Pending', value: applications.filter(a => a.status === 'PENDING' || a.status === 'APPLIED').length, icon: Clock, color: 'warning' },
    { title: 'Accepted', value: applications.filter(a => a.status === 'ACCEPTED' || a.status === 'HIRED').length, icon: CheckCircle2, color: 'success' },
    { title: 'Rejected', value: applications.filter(a => a.status === 'REJECTED').length, icon: XCircle, color: 'danger' },
  ];

  if (loading && applications.length === 0) {
    return (
      <div className="dashboard-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Preparing your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container container-fluid px-4 py-4">
      <div className="dashboard-header mb-4">
        <h1 className="fw-bold">Dashboard</h1>
        <p className="text-muted">Welcome back! Manage your applications and explore new roles.</p>
      </div>

      <div className="stats-grid mb-5">
        {stats.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Tabs Navigation */}
      <div className="dashboard-tabs mb-4">
        <button 
          className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          <Briefcase size={18} className="me-2" />
          Recommended Jobs
        </button>
        <button 
          className={`tab-btn ${activeTab === 'applications' ? 'active' : ''}`}
          onClick={() => setActiveTab('applications')}
        >
          <Clock size={18} className="me-2" />
          My Applications
        </button>
      </div>

      <div className="dashboard-content-tabs">
        {activeTab === 'applications' && (
          <section className="applications-section fade-in">
            <div className="section-header d-flex justify-content-between align-items-center mb-3">
              <h2 className="h4 mb-0">Recent Applications</h2>
              <button className="btn btn-link text-decoration-none" onClick={() => navigate('/my-applications')}>
                View All Applications
              </button>
            </div>
            <div className="applications-list">
              {applications.length > 0 ? (
                applications.slice(0, 10).map(app => (
                  <ApplicationCard 
                    key={app.applicationId || app.id} 
                    application={app} 
                    formatDate={formatDate}
                    onViewDetails={() => navigate('/my-applications')}
                  />
                ))
              ) : (
                <div className="empty-state card p-5 text-center bg-light">
                  <Briefcase size={48} className="text-muted mb-3 mx-auto" />
                  <p className="text-muted">You haven't applied to any jobs yet.</p>
                  <button className="btn btn-primary" onClick={() => setActiveTab('jobs')}>
                    Explore Jobs
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'jobs' && (
          <section className="jobs-section fade-in">
            {/* Modern Filter System */}
            <div className="filter-container">
              <div className="row g-4 align-items-end mb-4">
                <div className="col-lg-5">
                  <div className="search-bar-modern">
                    <Search size={20} className="search-icon" />
                    <input 
                      type="text" 
                      placeholder="Search roles, companies, or keywords..." 
                      value={searchTerm}
                      onChange={(e) => { setCurrentPage(0); setSearchTerm(e.target.value); }}
                    />
                    {searchTerm && (
                      <X 
                        size={18} 
                        className="text-muted cursor-pointer me-2" 
                        onClick={() => { setCurrentPage(0); setSearchTerm(''); }} 
                      />
                    )}
                  </div>
                </div>
                <div className="col-lg-7">
                  <div className="d-flex justify-content-lg-end align-items-center gap-3">
                    <span className="filter-reset-btn d-flex align-items-center gap-2" onClick={clearAllFilters}>
                      <X size={14} /> Clear All
                    </span>
                    <div className="d-flex gap-2">
                      <button 
                        className={`filter-pill ${selectedRemote === 'all' ? 'active' : ''}`}
                        onClick={() => { setCurrentPage(0); setSelectedRemote('all'); }}
                      >
                        All Locations
                      </button>
                      <button 
                        className={`filter-pill ${selectedRemote === 'remote' ? 'active' : ''}`}
                        onClick={() => { setCurrentPage(0); setSelectedRemote('remote'); }}
                      >
                        <Globe size={14} /> Remote
                      </button>
                      <button 
                        className={`filter-pill ${selectedRemote === 'onsite' ? 'active' : ''}`}
                        onClick={() => { setCurrentPage(0); setSelectedRemote('onsite'); }}
                      >
                        <Building2 size={14} /> On-site
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories Section */}
              <div className="category-filter-card mb-4">
                <span className="filter-label-main">Top Categories</span>
                <div className="filter-pills-row">
                  {categories.map(cat => (
                    <motion.button
                      key={cat.name}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`filter-pill ${selectedCategory === cat.name ? 'active' : ''}`}
                      onClick={() => { setCurrentPage(0); setSelectedCategory(cat.name); }}
                    >
                      {cat.icon}
                      {cat.name}
                    </motion.button>
                  ))}
                </div>

                <div className="border-top mt-3 pt-3">
                  <span className="filter-label-main">Job Type</span>
                  <div className="filter-pills-row mb-0">
                    {jobTypes.map(type => (
                      <motion.button
                        key={type}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`filter-pill ${selectedJobTypes.includes(type) ? 'active' : ''}`}
                        onClick={() => toggleJobType(type)}
                      >
                        {type}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Active Filter Indicators */}
              <AnimatePresence>
                {(selectedJobTypes.length > 0 || selectedRemote !== 'all' || selectedCategory !== 'All') && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="d-flex flex-wrap gap-2 mb-4 align-items-center"
                  >
                    <span className="small text-muted fw-bold me-2">ACTIVE FILTERS:</span>
                    {selectedJobTypes.map(type => (
                      <span key={type} className="badge bg-light text-dark border d-flex align-items-center gap-1 py-2 px-3 rounded-pill">
                        {type} <X size={12} className="cursor-pointer" onClick={() => toggleJobType(type)} />
                      </span>
                    ))}
                    {selectedRemote !== 'all' && (
                      <span className="badge bg-light text-dark border d-flex align-items-center gap-1 py-2 px-3 rounded-pill">
                        {selectedRemote === 'remote' ? 'Remote' : 'On-site'} 
                        <X size={12} className="cursor-pointer" onClick={() => setSelectedRemote('all')} />
                      </span>
                    )}
                    {selectedCategory !== 'All' && (
                      <span className="badge bg-light text-dark border d-flex align-items-center gap-1 py-2 px-3 rounded-pill">
                        {selectedCategory} 
                        <X size={12} className="cursor-pointer" onClick={() => setSelectedCategory('All')} />
                      </span>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="section-header d-flex justify-content-between align-items-center mb-4">
              <h2 className="h4 mb-0 d-flex align-items-center gap-2">
                <Briefcase className="text-brand" />
                {filteredJobs.length} Jobs for You
              </h2>
            </div>
            <div className="jobs-grid-container">
              {filteredJobs.length > 0 ? (
                <>
                  <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
                    {filteredJobs.map(job => (
                      <div className="col" key={job.jobPostId || job.id}>
                        <JobCard 
                          job={job} 
                          onApply={() => handleApply(job.jobPostId || job.id)}
                          isApplied={job.applicationStatus !== null}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {jobsPage.totalPages > 1 && (
                    <div className="pagination-bar mt-5 d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 bg-light rounded border">
                      <span className="text-muted small">Page {currentPage + 1} of {jobsPage.totalPages}</span>
                      <div className="btn-group">
                        <button 
                          className="btn btn-outline-primary btn-sm px-3" 
                          disabled={currentPage === 0}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          &larr; Previous
                        </button>
                        <button 
                          className="btn btn-outline-primary btn-sm px-3" 
                          disabled={currentPage + 1 >= jobsPage.totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next &rarr;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state card p-5 text-center bg-light">
                  <Search size={48} className="text-muted mb-3 mx-auto" />
                  <p className="text-muted">No matching jobs found at the moment.</p>
                  <button className="btn btn-outline-primary" onClick={clearAllFilters}>
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default JobSeekerDashboard;